import React, { useState } from 'react';
import axios from 'axios';
import { MessageCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API } from '../utils/apiConfig';

/**
 * Inline "Why?" button shown next to a translated error.
 * Calls Claude Haiku to explain the error in the user's language with concrete next steps.
 *
 * Props:
 *   code      — error code string (e.g. "email_exists")
 *   context   — optional extra context sent to Claude (safe subset only)
 *   testId    — optional data-testid suffix
 */
export const AIErrorHelper = ({ code, context = null, testId = 'ai-error-helper' }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [error, setError] = useState('');

  const askClaude = async () => {
    if (!code || loading) return;
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/orchestrator/claude/explain-error`, {
        code,
        lang: (i18n.language || 'de').slice(0, 2),
        context,
      });
      setExplanation(res.data.explanation || '');
    } catch (e) {
      setError(t('common.error'));
    }
    setLoading(false);
  };

  if (!code) return null;

  return (
    <div data-testid={testId} style={{ marginTop: 8 }}>
      {!explanation && !error && (
        <button
          type="button"
          onClick={askClaude}
          disabled={loading}
          data-testid={`${testId}-ask-btn`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
            background: 'rgba(167,139,250,0.12)',
            border: '1px solid rgba(167,139,250,0.35)',
            color: '#c4b5fd', fontSize: 12, fontWeight: 500,
          }}
        >
          {loading ? <Loader2 size={12} className="spin" /> : <MessageCircle size={12} />}
          {loading ? t('common.loading') : t('aiHelper.whyBtn')}
        </button>
      )}
      {explanation && (
        <div
          data-testid={`${testId}-text`}
          style={{
            padding: '12px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.55,
            background: 'rgba(15,23,42,0.92)',
            border: '1px solid rgba(167,139,250,0.45)',
            color: '#f8fafc', fontWeight: 500,
            boxShadow: 'inset 0 0 0 1px rgba(167,139,250,0.1)',
          }}
        >
          <div style={{ fontSize: 11, color: '#a78bfa', marginBottom: 6, fontWeight: 600, letterSpacing: 0.5 }}>
            ORI · AI
          </div>
          {explanation}
        </div>
      )}
      {error && <div style={{ color: '#ef4444', fontSize: 12 }}>{error}</div>}
    </div>
  );
};
